import React, { useState, useEffect, useRef } from "react";
import GoogleMaps from "../../common/GoogleMaps";
import Input from "../../common/FormInputs";
import PhoneFormInput from "../../common/PhoneInput";
import InputTextArea from "../../common/InputTextArea";
import Loader from "../../common/Loader";
import { uploadFile } from "../../../utils/s3";
import { ReactComponent as CameraIcon } from "../../../Assets/Icons/cameraIcon.svg";

import { useSelector, useDispatch } from "react-redux";
import Selector from "../../common/Selector";
import { getReasons, createReason } from "../../../services/onboardingService";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import {
  getFundraiserCategories,
  getSubCategoriesByCategoryId,
} from "../../../services/fundraiserService";

const EditProfile = ({
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  setPlaceId,
  setLogoLoading,
  logoLoading,
  resetKey,
  onCustomReasonCreated,
  secondaryMemberData,
  secondaryMemberActionLoading,
  onInviteSecondaryMember,
  onResendSecondaryMemberInvite,
  onCancelSecondaryMemberInvite,
  onRemoveSecondaryMember,
}) => {
  const resolveLogoValue = (profileValues = {}) => {
    const logoValue = profileValues?.logo ?? "";
    return typeof logoValue === "string" ? logoValue.trim() : String(logoValue || "").trim();
  };

  const [showMap, setShowMap] = useState(false);
  const [logoFileImage, setLogoFileImage] = useState();
  const [logoFileData, setLogoFileData] = useState();
  const [logoUrl, setLogoUrl] = useState();
  const [logoPreviewStyle, setLogoPreviewStyle] = useState({});
  const authUser = useSelector((state) => state?.authReducer?.tokenData?.user);
  const role = authUser?.role;
  const dispatch = useDispatch();
  const [allReasons, setAllReasons] = useState([]);
  const [newReason, setNewReason] = useState("");
  const [customReasonId, setCustomReasonId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [secondaryEmailInput, setSecondaryEmailInput] = useState("");
  const [secondaryMemberError, setSecondaryMemberError] = useState("");
  const [showRemoveSecondaryMemberModal, setShowRemoveSecondaryMemberModal] =
    useState(false);
  const viewerAccess =
    secondaryMemberData?.viewerAccess || authUser?.viewerAccess || "owner";
  const canManageSecondaryMember =
    secondaryMemberData?.canManageSecondaryMember ??
    authUser?.canManageSecondaryMember ??
    true;
  const secondaryMemberState =
    secondaryMemberData?.secondaryMemberState || "empty";
  const secondaryMember = secondaryMemberData?.secondaryMember || null;
  const secondaryInvite = secondaryMemberData?.secondaryInvite || null;
  const isSecondaryMemberViewer =
    role === "merchant" &&
    (viewerAccess === "member" || canManageSecondaryMember === false);
  const secondaryMemberReadonlyFieldClass = isSecondaryMemberViewer
    ? "secondary-member-readonly-field"
    : "";
  const secondaryMemberCardContent = (() => {
    if (isSecondaryMemberViewer) {
      return {
        title: "Secondary Member Access",
        subtitle: "You currently have merchant member access for this account.",
      };
    }

    if (
      secondaryMemberState === "pending" ||
      secondaryMemberState === "expired"
    ) {
      return {
        title: "Secondary Member Invitation",
        subtitle:
          secondaryMemberState === "expired"
            ? "The previous invitation has expired. You can resend or cancel it."
            : "An invitation has been sent and is awaiting acceptance.",
      };
    }

    if (secondaryMemberState === "active") {
      return {
        title: "Secondary Member Access",
        subtitle: "This account currently has one active secondary member.",
      };
    }

    return {
      title: "Attach Secondary Email",
      subtitle: "Attach a secondary email to provide account access.",
    };
  })();

  const getSecondaryMemberErrorMessage = (error) =>
    error?.message ||
    error?.data?.message ||
    "Unable to update secondary member access right now.";

  const handleAddEmail = async () => {
    const normalizedEmail = secondaryEmailInput.trim().toLowerCase();

    if (!normalizedEmail) {
      setSecondaryMemberError("Please enter an email address.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setSecondaryMemberError("Invalid email address format");
      return;
    }

    if (secondaryMemberState !== "empty") {
      setSecondaryMemberError(
        "You can only manage one secondary member at a time.",
      );
      return;
    }

    try {
      setSecondaryMemberError("");
      await onInviteSecondaryMember?.(normalizedEmail);
      setSecondaryEmailInput("");
    } catch (error) {
      setSecondaryMemberError(getSecondaryMemberErrorMessage(error));
    }
  };

  const handleSecondaryInviteResend = async () => {
    try {
      setSecondaryMemberError("");
      await onResendSecondaryMemberInvite?.();
    } catch (error) {
      setSecondaryMemberError(getSecondaryMemberErrorMessage(error));
    }
  };

  const handleSecondaryInviteCancel = async () => {
    try {
      setSecondaryMemberError("");
      await onCancelSecondaryMemberInvite?.();
      setSecondaryEmailInput("");
    } catch (error) {
      setSecondaryMemberError(getSecondaryMemberErrorMessage(error));
    }
  };

  const handleSecondaryMemberRemove = async () => {
    try {
      setSecondaryMemberError("");
      await onRemoveSecondaryMember?.();
      setShowRemoveSecondaryMemberModal(false);
      setSecondaryEmailInput("");
    } catch (error) {
      setSecondaryMemberError(getSecondaryMemberErrorMessage(error));
    }
  };

  useEffect(() => {
    setLogoFileImage(undefined);
    setLogoFileData(undefined);
    setLogoUrl(undefined);
    setLogoPreviewStyle({});
  }, [resetKey]);

  useEffect(() => {
    setSecondaryMemberError("");
    if (secondaryMemberState !== "empty") {
      setSecondaryEmailInput("");
    }
  }, [secondaryMemberState]);

  useEffect(() => {
    if (secondaryMemberState !== "active") {
      setShowRemoveSecondaryMemberModal(false);
    }
  }, [secondaryMemberState]);

  useEffect(() => {
    if (role === "fundraiser") {
      dispatch(getReasons())
        .unwrap()
        .then((res) => {
          if (res?.success && res?.data) setAllReasons(res.data);
        });
    }
  }, [dispatch, role]);

  useEffect(() => {
    setLogoPreviewStyle({});
  }, [logoFileImage, values?.logo]);

  // Fetch categories on mount
  useEffect(() => {
    if (role === "fundraiser") {
      dispatch(getFundraiserCategories())
        .unwrap()
        .then((res) => {
          if (res?.success && res?.data) setCategories(res.data);
        });
    }
  }, [dispatch, role]);

  // Normalize existing fundraiser causes to IDs once reasons are loaded
  useEffect(() => {
    if (role === "fundraiser" && allReasons.length > 0) {
      const selected = values.fundraiserCause || [];
      const hasNonIds = selected.some(
        (v) =>
          typeof v === "string" &&
          !/^[0-9a-fA-F]{24}$/.test(v) &&
          v !== "Other",
      );
      if (hasNonIds) {
        const normalized = selected.map((v) => {
          if (v === "Other") return v;
          if (/^[0-9a-fA-F]{24}$/.test(v)) return v;
          const found = allReasons.find((r) => r.reason === v);
          return found?._id || v;
        });
        setFieldValue("fundraiserCause", normalized);
      }
    }
  }, [role, allReasons, values.fundraiserCause, setFieldValue]);

  // Normalize sub-category IDs to titles for display once subcategories are loaded (only once, prevent loops)
  const hasNormalizedSubsRef = useRef(false);

  // Fetch subcategories when fundraiserCategory changes
  useEffect(() => {
    if (role === "fundraiser" && values.fundraiserCategory) {
      // Reset normalization flag when category changes
      hasNormalizedSubsRef.current = false;
      const selectedCat = categories.find(
        (cat) =>
          cat.title === values.fundraiserCategory ||
          cat._id === values.fundraiserCategory,
      );
      if (selectedCat?._id) {
        // Debug
        try {
          if (process.env.NODE_ENV !== "production")
            console.debug(
              "[EditProfile] Fetching subcategories for categoryId:",
              selectedCat._id,
              "title:",
              selectedCat.title,
            );
        } catch (_) {}
        dispatch(getSubCategoriesByCategoryId({ id: selectedCat._id }))
          .unwrap()
          .then((res) => {
            if (res?.success && res?.data) setSubCategories(res.data);
            // Debug
            try {
              if (process.env.NODE_ENV !== "production")
                console.debug(
                  "[EditProfile] subCategories fetched:",
                  (res?.data || []).map((s) => ({ id: s._id, title: s.title })),
                );
            } catch (_) {}
          });
      }
      // Hide subcategory and clear value if Community Organisations is selected
      if (values.fundraiserCategory === "Community Organisations") {
        setFieldValue("fundraiserSubCategory", []);
      }
    }
  }, [dispatch, role, values.fundraiserCategory, categories, setFieldValue]);

  // Normalize sub-category IDs to titles for display once subcategories are loaded (only once, prevent loops)
  useEffect(() => {
    if (
      role === "fundraiser" &&
      subCategories.length > 0 &&
      !hasNormalizedSubsRef.current
    ) {
      const selected = values.fundraiserSubCategory || [];
      // If any value looks like an ID, convert to title for display
      const hasIds = selected.some(
        (v) => typeof v === "string" && /^[0-9a-fA-F]{24}$/.test(v),
      );
      if (hasIds) {
        const normalized = selected
          .map((v) => {
            if (typeof v === "string" && /^[0-9a-fA-F]{24}$/.test(v)) {
              const found = subCategories.find((s) => s._id === v);
              return found ? found.title : v;
            }
            return v;
          })
          .filter((t) => typeof t === "string" && t.length > 0);
        if (normalized.length > 0) {
          hasNormalizedSubsRef.current = true;
          setFieldValue("fundraiserSubCategory", normalized, false);
        } else {
          hasNormalizedSubsRef.current = true;
        }
      } else {
        hasNormalizedSubsRef.current = true;
      }
    }
  }, [role, subCategories, setFieldValue]);

  // Prepare options
  const fundraiserCategoryOptions = categories.map((cat) => ({
    value: cat.title,
    label: cat.title,
  }));

  // Use sub-category titles as values so displayed tags show titles and can be removed reliably
  const subCategoryOptions = subCategories.map((sub) => ({
    value: sub.title,
    label: sub.title,
  }));
  // Debug current value/options
  try {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[EditProfile] SubCategory selector state", {
        valueIds: values?.fundraiserSubCategory,
        optionsCount: subCategoryOptions.length,
        firstOptions: subCategoryOptions.slice(0, 6),
      });
    }
  } catch (_) {}

  const validateImage = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (
      extFile === "jpg" ||
      extFile === "jpeg" ||
      extFile === "png" ||
      extFile === "svg"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleUploadLogo = async (event) => {
    const originalFile = event.target.files && event.target.files[0];
    if (!originalFile) return;
    const fileName = originalFile.name;

    // Optimize logo uploads for tiny UI surfaces like the sidebar, navbar, and profile chip.
    // We cap dimensions as well as file size so very high-res originals don't stay expensive to decode.
    const ONE_MB = 1024 * 1024;
    const MAX_DIMENSION = 512;
    const compressToUnder1MB = (file) =>
      new Promise((resolve) => {
        if (file.size <= ONE_MB) {
          const reader = new FileReader();
          reader.onload = () => {
            const img = new Image();
            img.onload = async () => {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              const width = img.width || MAX_DIMENSION;
              const height = img.height || MAX_DIMENSION;
              const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
              canvas.width = Math.max(1, Math.round(width * scale));
              canvas.height = Math.max(1, Math.round(height * scale));
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              const targetMime = "image/jpeg";
              const outName = file.name.replace(/\.(png|jpeg|jpg|svg)$/i, ".jpg");
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    resolve({ file, name: file.name });
                    return;
                  }
                  resolve({ file: new File([blob], outName, { type: targetMime }), name: outName });
                },
                targetMime,
                0.88
              );
            };
            img.onerror = () => resolve({ file, name: file.name });
            img.src = reader.result;
          };
          reader.onerror = () => resolve({ file, name: file.name });
          reader.readAsDataURL(file);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = async () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const width = img.width || MAX_DIMENSION;
            const height = img.height || MAX_DIMENSION;
            const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const targetMime = "image/jpeg";
            let quality = 0.92;
            const toBlobQ = (q) =>
              new Promise((res) => canvas.toBlob((b) => res(b), targetMime, q));
            let blob = await toBlobQ(quality);
            while (blob && blob.size > ONE_MB && quality > 0.6) {
              quality -= 0.05;
              blob = await toBlobQ(quality);
            }
            const outName = file.name.replace(/\.(png|jpeg|jpg)$/i, ".jpg");
            resolve({
              file: new File([blob], outName, { type: targetMime }),
              name: outName,
            });
          };
          img.onerror = () => resolve({ file, name: file.name });
          img.src = reader.result;
        };
        reader.onerror = () => resolve({ file, name: file.name });
        reader.readAsDataURL(file);
      });

    const { file: fileToUpload, name: finalName } =
      await compressToUnder1MB(originalFile);

    setLogoLoading && setLogoLoading(true);
    const s3Url = await uploadFile(fileToUpload, finalName);
    if (s3Url) {
      setLogoUrl(s3Url);
      setLogoLoading && setLogoLoading(false);
      // Persist the uploaded URL into the form state so save sends a valid logo URL
      try {
        setFieldValue("logo", s3Url, false);
      } catch (_) {}
    }

    const previewUrl = URL.createObjectURL(fileToUpload);
    setLogoFileImage(previewUrl);
    try {
      localStorage.setItem("profilePic", previewUrl);
      localStorage.setItem("LATEST_UPLOADED_LOGO", previewUrl);
    } catch (_) {}
    setLogoFileData(fileToUpload);
    setLogoPreviewStyle({});
  };

  const handleClearLogo = () => {
    setFieldValue("logo", "");
    setLogoFileImage(undefined);
    setLogoFileData(undefined);
    setLogoPreviewStyle({});
  };

  // const handleLocationClick = () => {
  //   setShowMap(true);
  // };

  const handleLocationSelect = (selectedLocation) => {
    setFieldValue("address", selectedLocation?.placeName);
    setPlaceId(selectedLocation?.placeId);
    setFieldValue("locationLat", selectedLocation?.lat);
    setFieldValue("locationLng", selectedLocation?.lng);
    setFieldValue("locationCity", selectedLocation?.city || "");
    setFieldValue("locationState", selectedLocation?.state || "");
    setFieldValue("locationCountry", selectedLocation?.country || "");
    setFieldValue("locationZip", selectedLocation?.zipCode || "");
  };
  console.log("Logo", `${process.env.REACT_APP_S3_URL + values?.logo}`);

  const StoreTypeCard = (
    <div
      className={`w-full bg-white border border-[#E4E3E4] rounded-[16px] p-[24px] shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 flex flex-col justify-center ${!values.isOnlineMerchant ? "" : "flex-grow"} ${secondaryMemberReadonlyFieldClass}`}
    >
      <div className="mb-5">
        <h3 className="font-poppins font-[600] tracking-tight text-[18px] text-[#303030]">
          Store Type
        </h3>
        <p className="font-poppins text-[13px] text-[#767676] mt-[4px] leading-relaxed">
          Let us know if your business operates exclusively online without a
          physical storefront.
        </p>
      </div>

      <div
        className={`flex items-center gap-3 p-4 rounded-[10px] border transition-all ${isSecondaryMemberViewer ? "cursor-not-allowed" : "cursor-pointer"} ${values.isOnlineMerchant ? "bg-[#E6F7FF] border-[#BCE4F9] shadow-[#BCE4F9]/50 shadow-sm" : "bg-[#F9F9F9] border-[#E5E5E5] hover:bg-[#F2F9FD]"}`}
        onClick={() => document.getElementById("isOnlineMerchant").click()}
      >
        <input
          type="checkbox"
          id="isOnlineMerchant"
          name="isOnlineMerchant"
          checked={values.isOnlineMerchant}
          onChange={(e) => {
            const checked = e.target.checked;
            setFieldValue("isOnlineMerchant", checked);
            if (checked) {
              setFieldValue("address", "Australia");
              setFieldValue("locationCity", "");
              setFieldValue("locationState", "");
              setFieldValue("locationCountry", "Australia");
              setFieldValue("locationZip", "");
              setFieldValue("locationLat", -33.8688);
              setFieldValue("locationLng", 151.2093);
            } else {
              // Clear values if unticking so user searches again
              setFieldValue("address", "");
              setFieldValue("locationLat", null);
              setFieldValue("locationLng", null);
            }
          }}
          className="peer hidden"
        />
        <div
          className={`w-6 h-6 border-[1.5px] rounded-[5px] flex items-center justify-center cursor-pointer transition-all duration-200 ${
            values.isOnlineMerchant
              ? "bg-[#00ADEF] border-[#00ADEF] shadow-sm"
              : "border-[#C4C4C4] bg-white"
          }`}
        >
          {values.isOnlineMerchant && (
            <svg
              className="w-4 h-4 transition"
              fill="none"
              stroke="white"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="isOnlineMerchant"
            className={`font-poppins text-[14px] font-[500] cursor-pointer select-none ${values.isOnlineMerchant ? "text-[#007EA7]" : "text-[#303030]"}`}
          >
            I deal in online offers and don't have a physical address.
          </label>
          <div
            className={`grid duration-300 ease-in-out ${values.isOnlineMerchant ? "grid-rows-[1fr] opacity-100 mt-[6px]" : "grid-rows-[0fr] opacity-0 mt-0"}`}
            style={{
              transitionProperty: "grid-template-rows, opacity, margin-top",
            }}
          >
            <div className="overflow-hidden">
              <p className="font-poppins text-[13px] text-[#767676] font-[400] select-none">
                Region defaults to Australia.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-4 md:px-0">
      <>
        <div className="flex flex-col lg:flex-row flex-wrap w-full mt-8">
          <Input
            type="text"
            label=""
            placeholder={`${
              role === "merchant"
                ? "Business Name"
                : role === "fundraiser"
                  ? "Fundraiser Name"
                  : "Name"
            }`}
            width="100%"
            radius="5px"
            InputDivClass="w-full lg:w-[47.5%]"
            name="businessName"
            data-field="businessName"
            value={values?.businessName}
            handleChange={(e) => {
              const val = e.target.value;
              // Allow only letters and spaces
              if (/^[A-Za-z\s]*$/.test(val)) {
                handleChange(e);
              }
            }}
            handleBlur={handleBlur}
            error={errors.businessName}
            touched={touched.businessName}
            inputClass={secondaryMemberReadonlyFieldClass}
          />
          <div className="lg:w-[5%]"></div>
          <Input
            type="text"
            label=""
            placeholder="Email"
            width="100%"
            radius="5px"
            InputDivClass="w-full mt-8 lg:mt-0 lg:w-[47.5%]"
            name="email"
            data-field="email"
            value={values?.email}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.email}
            touched={touched.email}
            readonly={true}
            inputClass={`cursor-not-allowed !bg-[#F9F9F9] ${secondaryMemberReadonlyFieldClass}`}
          />
        </div>
        {role !== "admin" && (
          <>
            <div className="flex flex-col lg:flex-row flex-wrap w-full mt-8">
              <Input
                type="text"
                label=""
                placeholder="Website URL"
                width="100%"
                radius="5px"
                InputDivClass="w-full lg:w-[47.5%]"
                name="websiteUrl"
                data-field="websiteUrl"
                value={values?.websiteUrl}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.websiteUrl}
                touched={touched.websiteUrl}
                inputClass={secondaryMemberReadonlyFieldClass}
              />
              <div className="lg:w-[5%]"></div>
              <PhoneFormInput
                label={""}
                divClassName="w-full mt-8 lg:mt-0 lg:w-[47.5%]"
                name="contact"
                data-field="contact"
                value={values?.contact}
                handleBlur={handleBlur}
                error={errors.contact}
                touched={touched.contact}
                setFieldValue={setFieldValue}
                inputStyle={{
                  width: "100%",
                  border: "none",
                }}
                buttonStyle={{ border: "none" }}
                buttonClass={`!rounded-l-[5px] !bg-[#D9D9D9] !w-[22%] md:!w-[10%] sm:!w-[15%] lg:!w-[13%] xl:!w-[10%] 2xl:!w-[10%] ${secondaryMemberReadonlyFieldClass}`}
                containerClass={`!rounded-[5px] ${secondaryMemberReadonlyFieldClass}`}
                inputClass={`lg:!h-[45px] md:!h-[45px] !rounded-[5px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[16px] sm:!text-[14px] !font-[400] !outline-none !font-poppins ${secondaryMemberReadonlyFieldClass}`}
                labelClass="mb-8"
                errorClass={
                  "text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1"
                }
              />
            </div>
            {/* Show Upload Logo for all roles - Moved below Website/Phone row */}
            <div
              className="flex mt-8 items-center gap-8 justify-start w-full logo-section"
              data-field="logo"
            >
              <div
                className={`relative h-[80px] w-[80px] rounded-[10px] border-2 border-solid border-[#E4E3E4] overflow-hidden bg-white flex justify-center items-center logo ${isSecondaryMemberViewer ? "cursor-not-allowed" : "cursor-pointer"}`}
                onClick={() => {
                  if (isSecondaryMemberViewer) return;
                  try {
                    const el = document.getElementById("fileInput");
                    if (el) el.click();
                  } catch (_) {}
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (isSecondaryMemberViewer) return;
                  if (e.key === "Enter" || e.key === " ") {
                    try {
                      const el = document.getElementById("fileInput");
                      if (el) el.click();
                    } catch (_) {}
                  }
                }}
              >
                {logoLoading === true && <Loader />}
                {logoFileImage !== undefined || resolveLogoValue(values) !== "" ? (
                  <img
                    src={(() => {
                      if (logoFileImage) return logoFileImage;
                      const v = values?.logo || "";
                      if (typeof v === "string" && /^https?:\/\//i.test(v))
                        return v;
                      return `${process.env.REACT_APP_S3_URL}${v}`;
                    })()}
                    alt="logo"
                    className="select-none"
                    width="80"
                    height="80"
                    decoding="async"
                    fetchPriority="high"
                    style={{
                      imageRendering: "auto",
                      borderRadius: "10px",
                      display: "block",
                      ...logoPreviewStyle,
                    }}
                    onLoad={(event) => {
                      try {
                        const { naturalWidth, naturalHeight } =
                          event.target || {};
                        if (!naturalWidth || !naturalHeight) {
                          setLogoPreviewStyle({});
                          return;
                        }
                        const widthRatio = naturalWidth / naturalHeight;
                        const heightRatio = naturalHeight / naturalWidth;
                        const widthMultiplier = 1.15;
                        const heightMultiplier = 1.08;
                        setLogoPreviewStyle({
                          width:
                            widthRatio >= 1
                              ? `${(widthRatio || 1) * 100 * widthMultiplier}%`
                              : `${100 * widthMultiplier}%`,
                          height:
                            heightRatio >= 1
                              ? `${(heightRatio || 1) * 100 * heightMultiplier}%`
                              : `${100 * heightMultiplier}%`,
                        });
                      } catch (error) {
                        setLogoPreviewStyle({});
                      }
                    }}
                  />
                ) : (
                  <CameraIcon />
                )}
              </div>
              <input
                type="file"
                id="fileInput"
                hidden
                accept=".jpg,.jpeg,.png,.svg"
                onChange={handleUploadLogo}
                onClick={(event) => {
                  event.target.value = null;
                }}
              />
              {logoFileImage !== undefined ||
              (values?.logo && values?.logo !== "") ? (
                <p
                  className={`font-poppins font-[400] text-[12px] md:text-[14px] text-[#535353] transition-colors ${isSecondaryMemberViewer ? "" : "cursor-pointer hover:text-red-500"}`}
                  onClick={() => {
                    if (isSecondaryMemberViewer) return;
                    handleClearLogo();
                  }}
                >
                  Clear
                </p>
              ) : (
                <div className="flex flex-col justify-start">
                  <p className="font-poppins font-[400] text-[12px] md:text-[14px] text-[#535353]">
                    {role === "fundraiser"
                      ? "Upload Fundraiser Logo"
                      : "Upload Merchant Logo"}
                  </p>
                  <p className="font-poppins font-[400] text-[10px] md:text-[12px] text-[#00adef]">
                    File type: must be an svg, jpeg, jpg, or png
                  </p>
                  {/* Removed size note per request */}
                </div>
              )}
            </div>
            {errors.logo && touched.logo ? (
              <div className="ms-1 mt-1 w-full flex justify-start items-start">
                <span className="text-red-600 text-[10px] sm:text-[11px] md:text-[12px] font-[600] text-start">
                  {errors.logo}
                </span>
              </div>
            ) : null}

            <div
              className={`flex flex-col lg:flex-row w-full gap-6 mt-8 mb-4 transition-all duration-500 items-stretch`}
            >
              <div
                className={`w-full lg:w-[60%] flex flex-col transition-all duration-500 ease-in-out`}
              >
                <div
                  className={`duration-500 ease-in-out overflow-hidden ${!values.isOnlineMerchant ? "max-h-[800px] opacity-100 mb-6" : "max-h-0 opacity-0 mb-0"}`}
                  data-field="address"
                >
                  <div className={`overflow-hidden border border-[#E4E3E4] rounded-[16px] bg-white shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 relative h-[380px] md:h-[480px] ${secondaryMemberReadonlyFieldClass}`}>
                    <style>{`
                      .edit-profile-map .gm-fullscreen-control {
                        top: 80px !important;
                      }
                      /* Force show desktop search bar on mobile */
                      .edit-profile-map .hidden.sm\\:block {
                        display: block !important;
                      }
                      /* Hide default mobile search button */
                      .edit-profile-map .sm\\:hidden {
                        display: none !important;
                      }
                    `}</style>
                    <div className="px-5 py-[18px] border-b border-[#E4E3E4] flex items-center justify-between bg-gradient-to-r from-[#FFFFFF] to-[#Fbfbfb]">
                      <div className="flex items-center gap-3">
                        <div className="w-[42px] h-[42px] rounded-full bg-[#E6F7FF] flex items-center justify-center">
                          <svg
                            className="w-5 h-5 text-[#00ADEF]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z"
                            ></path>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            ></path>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-poppins font-[600] tracking-tight text-[16px] text-[#303030]">
                            Store Location
                          </h3>
                          <p className="font-poppins text-[13px] text-[#767676] mt-[2px] leading-snug">
                            Drag pin or search to set physical address.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="edit-profile-map h-[320px] md:h-[400px] w-full relative">
                      <GoogleMaps
                        onLocationSelect={handleLocationSelect}
                        mapContainerStyle="swoodle-map h-full w-full relative"
                        searchContainerClass="!w-[60%] min-w-[240px] max-w-[340px] !absolute !top-[12px] right-[12px] z-10"
                        defaultAddress={values.address}
                        placeholder="Search for Business Address"
                        searchBarDivClassName="w-full flex items-center h-[52px] relative bg-white/95 backdrop-blur-sm rounded-[10px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white transition-all hover:bg-white overflow-hidden"
                        searchBarInputClassName="navSearchBar h-full w-[100%] ps-[4%] pe-[12%] text-[15px] font-[500] font-poppins outline-none bg-transparent text-[#303030] rounded-[10px] border-none placeholder:!text-[#999999]"
                        searchIconClassName="absolute right-[4%] w-[22px] h-[22px] text-[#00ADEF]"
                        defaultCenter={
                          values.locationLat && values.locationLng
                            ? {
                                lat: parseFloat(values.locationLat),
                                lng: parseFloat(values.locationLng),
                              }
                            : undefined
                        }
                      />
                    </div>
                    {errors.address && touched.address ? (
                      <div className="ms-3 my-2 w-full flex justify-start items-start relative z-[20]">
                        <span className="text-red-600 text-[10px] sm:text-[11px] md:text-[12px] font-[600] text-start bg-white/80 px-2 py-1 rounded-[4px]">
                          {errors.address}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Checkbox for online merchant - Hide for fundraiser */}
                {role !== "fundraiser" &&
                  values.isOnlineMerchant &&
                  StoreTypeCard}
              </div>
              <div
                className={`w-full flex flex-col transition-all duration-500 ease-in-out ${!values.isOnlineMerchant ? "lg:w-[40%]" : "lg:w-[50%]"} gap-6`}
              >
                {role !== "fundraiser" &&
                  !values.isOnlineMerchant &&
                  StoreTypeCard}
                {role === "merchant" && (
                  <div className="bg-white border border-[#E4E3E4] rounded-[16px] p-[24px] shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 h-full flex flex-col justify-start">
                    <div className="mb-6 border-b border-[#E4E3E4] pb-4">
                      <h3 className="font-poppins font-[600] tracking-tight text-[18px] text-[#303030]">
                        {secondaryMemberCardContent.title}
                      </h3>
                      <p className="font-poppins text-[13px] text-[#767676] mt-[4px] leading-relaxed">
                        {secondaryMemberCardContent.subtitle}
                      </p>
                    </div>

                    {secondaryMemberState === "empty" &&
                      !isSecondaryMemberViewer && (
                        <div className="relative w-full mt-auto">
                          <input
                            type="text"
                            placeholder="Enter email address..."
                            value={secondaryEmailInput}
                            onChange={(e) => {
                              setSecondaryEmailInput(e.target.value);
                              if (secondaryMemberError)
                                setSecondaryMemberError("");
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddEmail();
                              }
                            }}
                            className="h-[52px] w-[100%] px-[4%] pr-[110px] text-[15px] font-[400] font-poppins outline-none rounded-[10px] bg-[#Fbfbfb] border border-[#E4E3E4] focus:border-[#00ADEF] focus:bg-white focus:ring-[4px] focus:ring-[#00ADEF]/10 transition-all placeholder:!text-[#999999]"
                            disabled={secondaryMemberActionLoading}
                          />
                          <button
                            type="button"
                            onClick={handleAddEmail}
                            disabled={secondaryMemberActionLoading}
                            className={`absolute right-[6px] top-[6px] bottom-[6px] text-white px-[20px] rounded-[6px] font-poppins font-[500] text-[14px] transition-all flex items-center justify-center shadow-sm ${
                              secondaryMemberActionLoading
                                ? "bg-[#AFC7D2] cursor-not-allowed"
                                : "bg-[#00ADEF] hover:bg-[#0096D1]"
                            }`}
                          >
                            <span className="mb-[1px]">
                              {secondaryMemberActionLoading
                                ? "Adding..."
                                : "Add"}
                            </span>
                          </button>
                        </div>
                      )}

                    {secondaryMemberState === "pending" && (
                      <div className="flex flex-col h-full justify-between gap-4">
                        <div className="rounded-[10px] border border-[#E4E3E4] bg-[#FBFBFB] px-[18px] py-[16px]">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-poppins text-[13px] font-[500] text-[#303030]">
                                Invitation Sent To
                              </p>
                              <p className="font-poppins text-[14px] text-[#767676] mt-[6px] break-all">
                                {secondaryInvite?.email}
                              </p>
                            </div>
                            <span className="shrink-0 rounded-[16px] bg-[#E6F7FF] border border-[#BCE4F9] px-[12px] py-[4px] font-poppins text-[12px] font-[500] text-[#007EA7]">
                              Pending
                            </span>
                          </div>
                          {secondaryInvite?.expiresAt && (
                            <p className="font-poppins text-[12px] text-[#999999] mt-[12px]">
                              Expires{" "}
                              {new Date(
                                secondaryInvite.expiresAt,
                              ).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {!isSecondaryMemberViewer && (
                          <div className="flex items-center justify-end gap-3 mt-auto">
                            <button
                              type="button"
                              onClick={handleSecondaryInviteCancel}
                              disabled={secondaryMemberActionLoading}
                              className={`h-[40px] px-[18px] rounded-[6px] font-poppins text-[14px] font-[500] border transition-all ${
                                secondaryMemberActionLoading
                                  ? "bg-[#F2F2F2] text-[#A0A0A0] border-[#E0E0E0] cursor-not-allowed"
                                  : "bg-[#FFFFFF] text-[#767676] border-[#D9D9D9] hover:border-[#BFBFBF]"
                              }`}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {secondaryMemberState === "expired" && (
                      <div className="flex flex-col h-full justify-between gap-4">
                        <div className="rounded-[10px] border border-[#EFD8A6] bg-[#FFF9EC] px-[18px] py-[16px]">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-poppins text-[13px] font-[500] text-[#303030]">
                                Invitation Sent To
                              </p>
                              <p className="font-poppins text-[14px] text-[#767676] mt-[6px] break-all">
                                {secondaryInvite?.email}
                              </p>
                            </div>
                            <span className="shrink-0 rounded-[16px] bg-[#FFF4D6] border border-[#F4D58D] px-[12px] py-[4px] font-poppins text-[12px] font-[500] text-[#B7791F]">
                              Expired
                            </span>
                          </div>
                          {secondaryInvite?.expiresAt && (
                            <p className="font-poppins text-[12px] text-[#999999] mt-[12px]">
                              Expired{" "}
                              {new Date(
                                secondaryInvite.expiresAt,
                              ).toLocaleString()}
                            </p>
                          )}
                          {secondaryInvite?.visibleUntil && (
                            <p className="font-poppins text-[12px] text-[#999999] mt-[6px]">
                              Available to resend until{" "}
                              {new Date(
                                secondaryInvite.visibleUntil,
                              ).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {!isSecondaryMemberViewer && (
                          <div className="flex items-center justify-end gap-3 mt-auto">
                            <button
                              type="button"
                              onClick={handleSecondaryInviteCancel}
                              disabled={secondaryMemberActionLoading}
                              className={`h-[40px] px-[18px] rounded-[6px] font-poppins text-[14px] font-[500] border transition-all ${
                                secondaryMemberActionLoading
                                  ? "bg-[#F2F2F2] text-[#A0A0A0] border-[#E0E0E0] cursor-not-allowed"
                                  : "bg-[#FFFFFF] text-[#767676] border-[#D9D9D9] hover:border-[#BFBFBF]"
                              }`}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSecondaryInviteResend}
                              disabled={secondaryMemberActionLoading}
                              className={`h-[40px] px-[18px] rounded-[6px] font-poppins text-[14px] font-[500] text-white transition-all ${
                                secondaryMemberActionLoading
                                  ? "bg-[#AFC7D2] cursor-not-allowed"
                                  : "bg-[#00ADEF] hover:bg-[#0096D1]"
                              }`}
                            >
                              {secondaryMemberActionLoading
                                ? "Sending..."
                                : "Resend"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {secondaryMemberState === "active" && (
                      <div className="flex flex-col h-full justify-between gap-4">
                        <div className="rounded-[10px] border border-[#E4E3E4] bg-[#FBFBFB] px-[18px] py-[16px]">
                          {/* {isSecondaryMemberViewer && (
                            <div className="mb-[14px] rounded-[10px] bg-[#E6F7FF] border border-[#BCE4F9] px-[14px] py-[10px]">
                              <p className="font-poppins text-[13px] font-[500] text-[#007EA7]">
                                You are a merchant member for this account.
                              </p>
                            </div>
                          )} */}
                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <p className="font-poppins text-[12px] font-[500] text-[#999999] uppercase tracking-[0.3px]">
                                Name
                              </p>
                              <p className="font-poppins text-[14px] text-[#303030] mt-[4px] break-words">
                                {secondaryMember?.name || "Not available"}
                              </p>
                            </div>
                            <div>
                              <p className="font-poppins text-[12px] font-[500] text-[#999999] uppercase tracking-[0.3px]">
                                Email
                              </p>
                              <p className="font-poppins text-[14px] text-[#303030] mt-[4px] break-all">
                                {secondaryMember?.email || "Not available"}
                              </p>
                            </div>
                            <div>
                              <p className="font-poppins text-[12px] font-[500] text-[#999999] uppercase tracking-[0.3px]">
                                Phone
                              </p>
                              <p className="font-poppins text-[14px] text-[#303030] mt-[4px] break-words">
                                {secondaryMember?.phone || "Not available"}
                              </p>
                            </div>
                          </div>
                        </div>
                        {!isSecondaryMemberViewer && (
                          <div className="flex items-center justify-end mt-auto">
                            <button
                              type="button"
                              onClick={() =>
                                setShowRemoveSecondaryMemberModal(true)
                              }
                              disabled={secondaryMemberActionLoading}
                              className={`h-[40px] px-[18px] rounded-[6px] font-poppins text-[14px] font-[500] transition-all ${
                                secondaryMemberActionLoading
                                  ? "bg-[#F2F2F2] text-[#A0A0A0] border border-[#E0E0E0] cursor-not-allowed"
                                  : "bg-[#FFF2F0] text-[#D4380D] border border-[#FFD8BF] hover:bg-[#FFE7D9]"
                              }`}
                            >
                              {secondaryMemberActionLoading
                                ? "Removing..."
                                : "Remove"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {secondaryMemberError && (
                      <div className="mt-2 text-start">
                        <span className="text-red-500 text-[12px] font-[500] ms-1 font-poppins">
                          {secondaryMemberError}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {showRemoveSecondaryMemberModal && !isSecondaryMemberViewer && (
              <div className="fixed inset-0 z-[1200] flex items-center justify-center px-4">
                <div
                  className="absolute inset-0 bg-[#303030]/45"
                  onClick={() => setShowRemoveSecondaryMemberModal(false)}
                />
                <div className="relative z-[1] w-full max-w-[440px] rounded-[18px] bg-white p-[28px] shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
                  <h3 className="font-poppins text-[22px] font-[600] text-[#303030]">
                    Remove Secondary Member
                  </h3>
                  <p className="mt-3 font-poppins text-[14px] leading-relaxed text-[#767676]">
                    Are you sure you want to remove this secondary member?
                  </p>
                  <div className="mt-8 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowRemoveSecondaryMemberModal(false)}
                      disabled={secondaryMemberActionLoading}
                      className={`h-[42px] min-w-[110px] rounded-[8px] border font-poppins text-[14px] font-[500] transition-all ${
                        secondaryMemberActionLoading
                          ? "cursor-not-allowed border-[#E0E0E0] bg-[#F2F2F2] text-[#A0A0A0]"
                          : "border-[#D9D9D9] bg-white text-[#767676] hover:border-[#BFBFBF]"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSecondaryMemberRemove}
                      disabled={secondaryMemberActionLoading}
                      className={`h-[42px] min-w-[110px] rounded-[8px] border font-poppins text-[14px] font-[500] transition-all ${
                        secondaryMemberActionLoading
                          ? "cursor-not-allowed border-[#E0E0E0] bg-[#F2F2F2] text-[#A0A0A0]"
                          : "border-[#FFD8BF] bg-[#FFF2F0] text-[#D4380D] hover:bg-[#FFE7D9]"
                      }`}
                    >
                      {secondaryMemberActionLoading ? "Removing..." : "Remove"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <InputTextArea
              label={"About Business"}
              placeholder={
                "Please share with us the reasons why customers should choose to visit your establishment and make purchases/ use your services"
              }
              divClassName="relative mt-4 w-full"
              labelClassName="font-poppins text-[16px] font-[400] text-[#303030] mb-2"
              textareaClassName={`w-full h-[120px] md:h-[150px] px-3 md:px-[1%] py-2 md:pt-2 rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins whitespace-normal overflow-y-auto md:overflow-visible ${secondaryMemberReadonlyFieldClass}`}
              name="aboutBusiness"
              data-field="aboutBusiness"
              value={values.aboutBusiness}
              handleBlur={handleBlur}
              error={errors.aboutBusiness}
              touched={touched.aboutBusiness}
              setFieldValue={setFieldValue}
            />
            {role === "fundraiser" && (
              <>
                <Selector
                  value={(() => {
                    // Convert IDs to reason texts for display
                    const selectedValues = values.fundraiserCause || [];
                    return selectedValues.map((id) => {
                      if (id === "Other") return "Other";
                      const reason = allReasons.find((r) => r._id === id);
                      return reason ? reason.reason : id;
                    });
                  })()}
                  label={"Choose your fundraiser cause"}
                  labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
                  divClassName="mt-4 w-full multipleField"
                  selectClassName="w-full h-[45px] sm:h-[45px] md:h-[50px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer"
                  flexClassName="gap-1"
                  data-field="fundraiserCause"
                  options={[
                    ...allReasons
                      .filter((r) => {
                        const selectedValues = values.fundraiserCause || [];
                        // Check if this reason is already selected (by ID)
                        const isSelected = selectedValues.includes(r._id);
                        return !isSelected;
                      })
                      .map((r) => ({ label: r.reason, value: r.reason })),
                    { label: "Other", value: "Other" },
                  ]}
                  suffixIcon={
                    <span className="cursor-pointer">
                      <DownArrow />
                    </span>
                  }
                  placeholder={"Select or add cause"}
                  onChange={(value) => {
                    // Convert reason texts back to IDs for storage
                    const convertedValue = value.map((item) => {
                      if (item === "Other") return "Other";
                      const reason = allReasons.find((r) => r.reason === item);
                      return reason ? reason._id : item;
                    });
                    setFieldValue("fundraiserCause", convertedValue);
                  }}
                  handleBlur={handleBlur}
                  error={errors.fundraiserCause}
                  touched={touched.fundraiserCause}
                  multiple={true}
                  mode="tags"
                />
                {(values.fundraiserCause || []).includes("Other") && (
                  <div className="relative w-full mt-4">
                    <InputTextArea
                      label={""}
                      placeholder={"Write other reason..."}
                      divClassName="w-full"
                      labelClassName="invitationLabels"
                      textareaClassName="w-full h-[100px] px-4 pt-3 rounded-[10px] bg-[#F2F2F2] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins pr-24 pb-12"
                      name="newReason"
                      value={newReason}
                      handleBlur={handleBlur}
                      error={errors.newReason}
                      touched={touched.newReason}
                      setFieldValue={(_, val) => setNewReason(val)}
                      showCounter={false}
                    />
                    <div className="absolute bottom-4 right-4">
                      <button
                        type="button"
                        className="font-poppins text-[13px] sm:text-[12px] md:text-[13px] lg:text-[14px] text-center font-[500] h-[32px] sm:h-[32px] md:h-[36px] lg:h-[38px] rounded-[7px] px-3 bg-[#00ADEF] text-white"
                        disabled={
                          !newReason ||
                          newReason.trim() === "" ||
                          (values.fundraiserCause || []).includes(
                            newReason.trim(),
                          )
                        }
                        onClick={async () => {
                          if (
                            newReason &&
                            newReason.trim() !== "" &&
                            !(values.fundraiserCause || []).includes(
                              newReason.trim(),
                            )
                          ) {
                            try {
                              // Call the API to create the reason (for backend record)
                              const createResponse = await dispatch(
                                createReason({ reason: newReason.trim() }),
                              )?.unwrap();

                              if (createResponse?.success === true) {
                                // Store the created reason ID
                                setCustomReasonId(createResponse?.data?._id);

                                // Notify parent component about the custom reason
                                if (onCustomReasonCreated) {
                                  onCustomReasonCreated(
                                    createResponse?.data?._id,
                                  );
                                }

                                // Remove "Other" and add the new reason text temporarily for display
                                const updated = [
                                  ...(values.fundraiserCause || []).filter(
                                    (r) => r !== "Other" && r.trim() !== "",
                                  ),
                                  newReason.trim(),
                                ];
                                setFieldValue("fundraiserCause", updated);
                                setNewReason("");
                              }
                            } catch (error) {
                              console.error(
                                "Failed to create custom reason",
                                error,
                              );
                            }
                          }
                        }}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            {role === "fundraiser" && (
              <>
                <Selector
                  value={values.fundraiserCategory ?? undefined}
                  label={"Select Fundraiser Category"}
                  labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
                  divClassName="mt-4 w-full"
                  selectClassName="w-full h-[45px] sm:h-[45px] md:h-[50px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer"
                  flexClassName="gap-1"
                  data-field="fundraiserCategory"
                  options={fundraiserCategoryOptions}
                  suffixIcon={
                    <span className="cursor-pointer">
                      <DownArrow />
                    </span>
                  }
                  placeholder={"Select Fundraiser Category"}
                  onChange={(val) => setFieldValue("fundraiserCategory", val)}
                  handleBlur={handleBlur}
                  error={errors.fundraiserCategory}
                  touched={touched.fundraiserCategory}
                  multiple={false}
                />
                {/* Only show Sub Category if not Community Organisations */}
                {values.fundraiserCategory !== "Community Organisations" && (
                  <>
                    <Selector
                      value={values.fundraiserSubCategory || []}
                      label={"Select Fundraiser Sub Category"}
                      labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
                      divClassName="mt-4 w-full multipleField"
                      selectClassName="w-full min-h-[45px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer !h-auto"
                      flexClassName="gap-1"
                      data-field="fundraiserSubCategory"
                      options={subCategoryOptions}
                      suffixIcon={
                        <span className="cursor-pointer">
                          <DownArrow />
                        </span>
                      }
                      placeholder={"Select Fundraiser Sub Category"}
                      onChange={(value) => {
                        // Debug
                        try {
                          if (process.env.NODE_ENV !== "production")
                            console.debug(
                              "[EditProfile] SubCategory onChange value:",
                              value,
                            );
                        } catch (_) {}
                        setFieldValue("fundraiserSubCategory", value);
                      }}
                      handleBlur={handleBlur}
                      error={errors.fundraiserSubCategory}
                      touched={touched.fundraiserSubCategory}
                      multiple={true}
                    />
                    <style>{`
                      /* Fix for mulit-select tags overflow on mobile - Horizontal Scrolling Chips */
                      .multipleField .ant-select-selection-overflow {
                        display: flex;
                        flex-wrap: nowrap !important;
                        overflow-x: auto;
                        overflow-y: hidden;
                        max-width: 100%;
                        scrollbar-width: none; /* Firefox */
                        -ms-overflow-style: none; /* IE/Edge */
                        padding-right: 20px; /* Spacer for last item */
                      }
                      .multipleField .ant-select-selection-overflow::-webkit-scrollbar {
                        display: none; /* Chrome/Safari/Opera */
                      }
                      .multipleField .ant-select-selection-overflow-item {
                        flex: 0 0 auto !important;
                      }
                      .multipleField .ant-select-selector {
                         height: auto !important;
                         min-height: 45px;
                         padding-top: 4px;
                         padding-bottom: 4px;
                         padding-left: 8px;
                         padding-right: 8px;
                         overflow: hidden;
                      }
                    `}</style>
                    {/* --- NEW FIELDS BELOW --- */}
                    <div className="w-full mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                      <Input
                        type="text"
                        label="Organisation Name"
                        placeholder="Enter Organisation Name"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="organisationName"
                        value={values.organisationName}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.organisationName}
                        touched={touched.organisationName}
                      />
                      <Input
                        type="text"
                        label="ABN"
                        placeholder="Enter ABN"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="abn"
                        value={values.abn}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.abn}
                        touched={touched.abn}
                      />
                      <Input
                        type="text"
                        label="Charity Registration Number"
                        placeholder="Enter Charity Registration Number"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="charityRegistrationNumber"
                        value={values.charityRegistrationNumber}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.charityRegistrationNumber}
                        touched={touched.charityRegistrationNumber}
                      />
                      <Input
                        type="email"
                        label="Accounts Email"
                        placeholder="Enter Accounts Email"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="accountsEmail"
                        value={values.accountsEmail}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.accountsEmail}
                        touched={touched.accountsEmail}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
    </div>
  );
};

export default EditProfile;
